import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from './ui/Button';
import Alert from './ui/Alert';
import Modal from './ui/Modal';

// Modal afișat la prima conectare (sau după schimbarea versiunii politicii).
// Userul trebuie să citească politica până la final ȘI să bifeze că a citit.
// Dacă apasă "Accept" fără să bifeze, scrollăm automat la checkbox și îl evidențiem cu roșu.
// Refuzul → logout + mesaj. Se poate reconecta ulterior, modalul reapare.
const GDPRModal = ({ onAccepted }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRefuse, setShowRefuse] = useState(false);
  const [reachedBottom, setReachedBottom] = useState(false);
  const [highlightCheckbox, setHighlightCheckbox] = useState(false);

  const scrollRef = useRef(null);
  const checkboxRowRef = useRef(null);

  // Detectează când userul a citit până jos (toleranță 24px pentru padding/border).
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const onScroll = () => {
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 24) {
        setReachedBottom(true);
      }
    };
    onScroll(); // posibil să încapă deja întreg textul în viewport
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => node.removeEventListener('scroll', onScroll);
  }, []);

  const flashHighlight = () => {
    setHighlightCheckbox(true);
    // Animație pulsatorie scurtă; ringul roșu rămâne până la bifare.
    window.setTimeout(() => setHighlightCheckbox(false), 1800);
  };

  const handleAccept = async () => {
    // Caz 1: userul nu a derulat până la final → împinge spre checkbox și avertizează.
    if (!reachedBottom) {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      }
      setReachedBottom(true);
      setError(t('gdpr.mustReadAll'));
      flashHighlight();
      return;
    }
    // Caz 2: a citit dar nu a bifat → scroll la checkbox + chenar roșu pulsatoriu.
    if (!checked) {
      if (checkboxRowRef.current) {
        checkboxRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setError(t('gdpr.mustCheck'));
      flashHighlight();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/auth/accept-gdpr');
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.gdprAcceptedAt = res.data.gdprAcceptedAt;
      stored.gdprVersion = res.data.gdprVersion;
      localStorage.setItem('user', JSON.stringify(stored));
      if (onAccepted) onAccepted();
    } catch (err) {
      setError(err.response?.data?.error || t('gdpr.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefuse = () => setShowRefuse(true);
  const handleConfirmRefuse = () => logout();

  if (showRefuse) {
    return (
      <Modal
        open={true}
        onClose={() => setShowRefuse(false)}
        closable={false}
        title={t('gdpr.refuseTitle')}
        size="md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button variant="secondary" onClick={() => setShowRefuse(false)}>
              {t('gdpr.refuseBack')}
            </Button>
            <Button variant="danger" onClick={handleConfirmRefuse}>
              {t('gdpr.refuseConfirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('gdpr.refuseP1')}</p>
          <p>{t('gdpr.refuseP2')}</p>
          <p className="font-medium">{t('gdpr.refuseP3')}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={true}
      onClose={() => {}}
      closable={false}
      title={t('gdpr.title')}
      size="2xl"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end w-full">
          <Button variant="outline" onClick={handleRefuse} disabled={loading}>
            {t('gdpr.refuse')}
          </Button>
          <Button variant="primary" onClick={handleAccept} loading={loading} disabled={loading}>
            {t('gdpr.accept')}
          </Button>
        </div>
      }
    >
      <div
        ref={scrollRef}
        className="space-y-4 text-sm text-slate-700 dark:text-slate-300 max-h-[60vh] overflow-y-auto pr-2"
      >
        <p className="text-slate-500 dark:text-slate-400 italic">{t('gdpr.intro')}</p>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            1. Operatorii de date și rolurile lor
          </h3>
          <p>
            <strong>Angajatorul dumneavoastră</strong> este <strong>operatorul principal</strong> al
            datelor (Art. 4 pct. 7 GDPR): el stabilește scopurile și mijloacele prelucrării pentru
            a-și îndeplini obligațiile legale de SSM. <strong>Firma SSM externă</strong> (dacă
            angajatorul folosește servicii externalizate) acționează ca operator distinct sau ca
            persoană împuternicită, după caz.
          </p>
          <p className="mt-2">
            <strong>Operatorul Platformei</strong> (furnizorul SaaS) prelucrează datele exclusiv în
            calitate de <strong>persoană împuternicită (Art. 28 GDPR)</strong>, acționând în baza
            instrucțiunilor scrise ale angajatorului (operator), prin Acordul de prelucrare a
            datelor (DPA) semnat cu fiecare firmă Client. Furnizorul Platformei nu stabilește el
            însuși scopurile prelucrării.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            2. Temeiul legal al prelucrării
          </h3>
          <p>Datele dumneavoastră sunt prelucrate în temeiul:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>
              <strong>Obligației legale</strong> a angajatorului și a firmei SSM (art. 6 alin. 1
              lit. c din Regulamentul UE 2016/679), în special Legea nr. 319/2006 a securității și
              sănătății în muncă, Legea nr. 307/2006 privind apărarea împotriva incendiilor, H.G.
              nr. 1425/2006 și H.G. nr. 955/2010.
            </li>
            <li>
              <strong>Executării contractului individual de muncă</strong> (art. 6 alin. 1 lit. b
              din Regulament).
            </li>
            <li>
              <strong>Interesului legitim</strong> al operatorilor de a dovedi îndeplinirea
              obligațiilor legale de instruire (art. 6 alin. 1 lit. f din Regulament).
            </li>
          </ul>
          <p className="mt-2">
            Prin urmare, prelucrarea <strong>nu depinde de consimțământul dumneavoastră</strong>,
            iar confirmarea de la finalul acestui document reprezintă{' '}
            <strong>luarea la cunoștință a prezentei informări</strong> (art. 13 din Regulament), nu
            un consimțământ în sensul art. 6 alin. (1) lit. a). Aceste date sunt necesare pentru
            îndeplinirea obligațiilor legale de SSM și pentru executarea contractului de muncă.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            3. Categoriile de date prelucrate
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Date de identificare: nume, prenume, email, telefon.</li>
            <li>
              <strong>Codul numeric personal (CNP) — regim special.</strong> Conform{' '}
              <strong>Art. 4 alin. (1) din Legea 190/2018</strong>, CNP este un identificator
              național cu protecție specială. Prelucrarea se face în temeiul obligației legale de
              identificare unică în fișele individuale de instruire (Anexa 11 din HG 1425/2006) și
              al executării contractului individual de muncă. CNP este stocat{' '}
              <strong>criptat AES-256-GCM</strong> la nivel de coloană, accesul la decriptare este
              auditat, iar CNP nu este niciodată afișat integral în interfață, în URL-uri sau în
              loguri. Conform garanțiilor cerute de{' '}
              <strong>art. 4 alin. (2) din Legea 190/2018</strong>, operatorii desemnează un
              responsabil cu protecția datelor (DPO), stabilesc termene de stocare și instruiesc
              periodic personalul cu drept de acces.
            </li>
            <li>
              Date profesionale: funcția, departamentul, data angajării, calificarea, autorizații
              speciale, legitimație/marcă.
            </li>
            <li>
              Date privind domiciliul și locul de muncă declarat, traseul zilnic și mijlocul de
              transport (necesare pentru evaluarea riscurilor și a accidentelor de traseu).
            </li>
            <li>Data și locul nașterii, grupa sanguină (opțional).</li>
            <li>
              Semnătura electronică digitalizată, utilizată la confirmarea instruirilor și a fișelor
              SSM/SU.
            </li>
            <li>Rezultatele instruirilor, testelor și evaluărilor, certificatele obținute.</li>
            <li>
              Date tehnice de jurnal: adresă IP, tip dispozitiv/browser, data și ora accesului,
              istoric acțiuni, colectate în scop de securitate și audit.
            </li>
          </ul>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            4. Scopurile prelucrării
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Organizarea și desfășurarea instruirilor SSM și de situații de urgență.</li>
            <li>Generarea și arhivarea fișelor individuale de instruire și a certificatelor.</li>
            <li>Evidența admiterilor la lucru și a intervalelor de repetare a instructajelor.</li>
            <li>
              Emiterea rapoartelor pentru Inspecția Muncii (ITM) și pentru alte autorități
              competente.
            </li>
            <li>Securitatea platformei, prevenirea fraudei și auditul activității.</li>
          </ul>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            5. Destinatarii și sursele datelor
          </h3>
          <p>
            Datele pot fi comunicate: angajatorului dumneavoastră, firmei SSM parteneră,
            inspectorilor Inspecției Teritoriale de Muncă (ITM) la solicitarea acestora, altor
            autorități publice atunci când există o obligație legală, precum și furnizorilor de
            servicii IT (hosting, mentenanță) care acționează ca persoane împuternicite în baza unor
            contracte ce asigură un nivel corespunzător de protecție. Datele{' '}
            <strong>nu sunt vândute și nu sunt transferate în scopuri de marketing</strong>.
          </p>
          <p className="mt-2">
            <strong>Sursele datelor:</strong> majoritatea datelor provin direct de la dumneavoastră
            sau de la angajator. Datele de identificare ale firmei (denumire, sediu, cod de
            înregistrare, stare TVA) pot fi preluate sau verificate din{' '}
            <strong>registrele publice ale ANAF</strong>.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            6. Perioada de stocare
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Fișe individuale de instruire, certificate, semnături:</strong> pe toată
              durata raportului de muncă <strong>plus 10 ani</strong> după încetare, termen necesar
              pentru a putea dovedi îndeplinirea obligațiilor legale de instruire la controalele
              Inspecției Muncii (ITM) și ale altor autorități competente, precum și pentru a
              răspunde unor eventuale litigii de muncă.
            </li>
            <li>
              <strong>Date privind expunerea la agenți specifici:</strong>{' '}
              <strong>40 de ani</strong> după încetarea expunerii — DOAR pentru expunere la agenți
              cancerigeni/mutageni (HG 1093/2006), azbest (HG 1875/2005), agenți biologici grupa 3/4
              (HG 1092/2006) sau radiații ionizante. În aceste cazuri fișa face parte din dosarul
              medical al expunerii.
            </li>
            <li>
              <strong>Cont utilizator</strong> (identificare, e-mail, parolă hash, semnătură): pe
              durata existenței contului; la ștergere datele de identificare se anonimizează.
            </li>
            <li>
              <strong>Date tehnice de jurnal (audit log, IP):</strong> 5 ani de la generarea
              evenimentului (extins dacă există o anchetă în curs).
            </li>
          </ul>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            7. Drepturile dumneavoastră
          </h3>
          <p>Conform Regulamentului UE 2016/679 (GDPR) și Legii nr. 190/2018, aveți dreptul la:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>
              <strong>Acces</strong> la datele personale prelucrate.
            </li>
            <li>
              <strong>Rectificarea</strong> datelor inexacte sau incomplete.
            </li>
            <li>
              <strong>Ștergerea</strong> datelor („dreptul de a fi uitat"), în limitele permise de
              lege.
            </li>
            <li>
              <strong>Restricționarea</strong> prelucrării, în situațiile prevăzute de lege.
            </li>
            <li>
              <strong>Portabilitatea</strong> datelor furnizate, în format structurat.
            </li>
            <li>
              <strong>Opoziție</strong> la prelucrare, atunci când temeiul este interesul legitim.
            </li>
            <li>
              <strong>Depunerea unei plângeri</strong> la Autoritatea Națională de Supraveghere a
              Prelucrării Datelor cu Caracter Personal (ANSPDCP, www.dataprotection.ro).
            </li>
          </ul>
          <p className="mt-2">
            <strong>Decizii automate:</strong> nu luăm decizii automate care să producă efecte
            juridice asupra dumneavoastră (art. 22 din Regulament). Scorarea testelor este automată,
            însă admiterea la lucru este decisă de o persoană responsabilă.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            8. Securitatea și localizarea datelor
          </h3>
          <p>
            Aplicăm măsuri tehnice și organizatorice pentru protejarea datelor: parole stocate ca
            hash bcrypt, CNP criptat cu AES-256-GCM, conexiuni criptate HTTPS, control de acces pe
            bază de rol și jurnalizare a acțiunilor sensibile.
          </p>
          <p className="mt-2">
            Datele sunt găzduite pe servere situate în{' '}
            <strong>Uniunea Europeană / Spațiul Economic European</strong>. Nu transferăm date cu
            caracter personal în afara SEE; dacă acest lucru ar deveni necesar, se va face numai în
            baza unor garanții adecvate (clauze contractuale standard, art. 46 din Regulament).
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            9. Contact pentru exercitarea drepturilor
          </h3>
          <p>
            Pentru exercitarea oricăruia dintre drepturile de mai sus, puteți contacta
            administratorul companiei dumneavoastră sau responsabilul cu protecția datelor (DPO) al
            operatorului. Cererile sunt soluționate în termenul legal de 30 de zile, cu posibilitate
            de prelungire în cazuri justificate.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            10. Modificarea politicii
          </h3>
          <p>
            În cazul modificării acestei Politici, vă vom solicita să confirmați din nou luarea la
            cunoștință la următoarea conectare. Versiunea curentă este afișată mai jos împreună cu
            data confirmării.
          </p>
        </section>

        <div
          ref={checkboxRowRef}
          className={`pt-3 border-t border-slate-200 dark:border-slate-700 -mx-2 px-3 py-3 rounded-lg transition-all ${
            !checked && (highlightCheckbox || error)
              ? 'border-2 border-danger-500 bg-danger-50 dark:bg-danger-900/30 ring-4 ring-danger-200 dark:ring-danger-800'
              : 'border border-transparent'
          } ${highlightCheckbox ? 'animate-pulse' : ''}`}
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                setChecked(e.target.checked);
                if (e.target.checked) {
                  setError(null);
                  setHighlightCheckbox(false);
                }
              }}
              className={`mt-1 w-5 h-5 rounded focus:ring-primary-500 ${
                !checked && (highlightCheckbox || error)
                  ? 'border-2 border-danger-500 text-danger-600'
                  : 'border-slate-300 text-primary-600'
              }`}
            />
            <span
              className={`text-sm font-medium ${
                !checked && (highlightCheckbox || error)
                  ? 'text-danger-700 dark:text-danger-300'
                  : 'text-slate-900 dark:text-slate-100'
              }`}
            >
              {t('gdpr.checkboxLabel')}
            </span>
          </label>
        </div>

        {error && <Alert type="error">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default GDPRModal;
